import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { stripePromise } from 'components/UpdatePaymentMethodModal/UpdatePaymentMethodModal'

import Styles from './CreditCardForm.module.scss'

import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { useSetupIntent } from 'components/TimeTriggerPaywall/screens/Checkout/Checkout'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

import { Modal } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Calendar2, Images } from 'blaze-ui/icons'
import { BarChartSquareUp01 } from 'blaze-ui/icons/16/BarChartSquareUp01'
import { StripePrice } from '../../../types'
import { Card } from 'blaze-ui/icons'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { sendToApi } from 'api/client'
import { CardholderNameInput } from 'screens/LoginOrSignup/components/Blaze/StartFreeTrial/components/FreeTrialCardForm/FreeTrialCardForm'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'

export const UPGRADED_FROM_WORKSPACE_ID = 'upgraded_from_workspace_id'

export const PaymentForm = observer(
  ({
    handleSubmit,
    showCreditCardForm,
    setIsProcessing,
    children,
  }: PropsWithChildren<{
    showCreditCardForm: boolean
    setIsProcessing: (value: boolean) => void
    handleSubmit: (paymentMethodId?: string) => void
  }>) => {
    const stripe = useStripe()
    const elements = useElements()
    const [ready, setReady] = useState(false)
    const [isCardHolderEmpty, setIsCardHolderEmpty] = useState(false)
    const [isCardPayment, setIsCardPayment] = useState(true)
    const cardHolderNameRef = useRef<HTMLInputElement>(null)

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      setIsProcessing(true)
      e.preventDefault()

      if (!stripe || !elements) {
        setIsProcessing(false)
        return
      }

      if (showCreditCardForm) {
        // Validate cardholder name if using card payment
        const cardHolderName = cardHolderNameRef.current?.value || ''
        if (isCardPayment && cardHolderName === '') {
          setIsCardHolderEmpty(true)
          setIsProcessing(false)
          return
        }

        const { setupIntent, error: setupError } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            // eslint-disable-next-line no-restricted-properties
            return_url: window.location.href,
            payment_method_data: {
              billing_details: { name: cardHolderName },
            },
          },
          redirect: 'if_required',
        })

        if (setupError || !setupIntent?.payment_method) {
          showToastMessage('Payment method not found. Try again.', 'error')
          setIsProcessing(false)
          return
        }

        const paymentMethodId = setupIntent.payment_method as string
        handleSubmit(paymentMethodId)
      } else {
        handleSubmit()
      }
    }

    return (
      <form onSubmit={onSubmit}>
        {showCreditCardForm && (
          <div className={Styles.inputs}>
            <CardholderNameInput
              ref={cardHolderNameRef}
              isInvalid={isCardHolderEmpty}
              isDisabled={!ready}
              setIsInvalid={setIsCardHolderEmpty}
            />
            <PaymentElement
              onReady={() => setReady(true)}
              options={{
                paymentMethodOrder: ['card', 'apple_pay', 'google_pay', 'cashapp'],
                fields: {
                  billingDetails: {
                    name: 'never', // Disable Stripe's name field, we use our own
                  },
                },
              }}
              onChange={(e) => {
                const selectedPaymentMethod = e?.value?.type
                setIsCardPayment(selectedPaymentMethod === 'card')
              }}
            />
          </div>
        )}
        {children}
      </form>
    )
  },
)

export interface CreditCardFormProps {
  chosenPrice: StripePrice
  onBack: () => void
  onSuccess: () => void
}

export const CreditCardForm = ({ chosenPrice, onBack, onSuccess }: CreditCardFormProps) => {
  const [showCreditCardForm, setShowCreditCardForm] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { data: clientSecret, isLoading } = useSetupIntent(currentWorkspaceId)
  const { data: currentPlan } = useCurrentPlan()

  useEffect(() => {
    if (!currentPlan) return

    if (!currentPlan.card) {
      setShowCreditCardForm(true)
    }
  }, [currentPlan])

  if (isLoading || !clientSecret) return null

  const handleSubmit = async (paymentMethodId?: string) => {
    const dismiss = showToastMessage('Processing...', 'loading', undefined, 0)
    const res = await sendToApi(`/w/${currentWorkspaceId}/virtual_marketers/update_subscription`, {
      body: { payment_method_id: paymentMethodId, price_id: chosenPrice.id },
      method: 'PUT',
    })
    dismiss()
    setIsProcessing(false)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      onSuccess()
    } else {
      showToastMessage('An error occurred. Try again. Contact support if the issue persists.', 'error')
    }
  }

  return (
    <>
      <Modal.Header>
        <div className={Styles.header}>
          <Modal.BackButton onPress={onBack} />
          <Heading level={3}>Checkout</Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className={Styles.chosenPriceContent}>
          <div className={Styles.info}>
            <div className={Styles.planDescription}>
              <Heading level={4} color="var(--purple-70)">
                {chosenPrice.recurring.interval_count === 1
                  ? 'Month to month'
                  : `${chosenPrice.recurring.interval_count} month`}{' '}
                plan
              </Heading>
              <div className={Styles.planPriceDescription}>
                <Text variant="placeholder" color="var(--purple-70)" fontWeight="700">
                  ${chosenPrice.unit_amount / 100}
                </Text>
                {chosenPrice.metadata.percent_off && (
                  <Text variant="label" color="var(--purple-70)">
                    ({chosenPrice.metadata.percent_off}% off)
                  </Text>
                )}
              </div>
            </div>
            <div className={Styles.planFeatures}>
              <div className={Styles.planFeature}>
                <Calendar2 color="var(--purple-50)" size={16} />
                <Text variant="secondary">Auto-creates and schedules content from your Content Plan.</Text>
              </div>
              <div className={Styles.planFeature}>
                <Images color="var(--purple-50)" size={16} />
                <Text variant="secondary">Delivers weekly content for easy review and edits.</Text>
              </div>
              <div className={Styles.planFeature}>
                <BarChartSquareUp01 color="var(--purple-50)" size={16} />
                <Text variant="secondary">Tracks results live and updates every login.</Text>
              </div>
            </div>
          </div>
          <div className={Styles.creditCardForm}>
            {currentPlan?.card && !showCreditCardForm && (
              <div className={Styles.cardOnFileContainer}>
                <p className={Styles.cardOnFileLabel}>Saved payment method</p>
                <div className={Styles.cardOnFile}>
                  <div className={Styles.cardDetails}>
                    <Card size={20} />
                    <div className={Styles.cardNumber}>
                      <p className={Styles.obscured}>•••• •••• ••••</p>
                      <p>{currentPlan.card.last_four}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onPress={() => setShowCreditCardForm(true)}>
                    Change
                  </Button>
                </div>
              </div>
            )}
            <div className={Styles.creditCardForm}>
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#6159FF',
                      colorBackground: '#ffffff',
                      colorText: '#1A1523',
                    },
                  },
                }}
              >
                <PaymentForm
                  handleSubmit={handleSubmit}
                  showCreditCardForm={showCreditCardForm}
                  setIsProcessing={setIsProcessing}
                >
                  <div className={Styles.buttonContainer}>
                    <Button variant="tertiary" size="lg" onPress={onBack}>
                      Back
                    </Button>
                    <div className={Styles.rightButtonsContainer}>
                      <Text variant="placeholder" color="var(--dark-80)">
                        ${chosenPrice.unit_amount / 100}{' '}
                        {chosenPrice.recurring.interval_count > 1
                          ? `every ${chosenPrice.recurring.interval_count} months`
                          : 'monthly'}
                      </Text>
                      <Button size="lg" type="submit" isDisabled={isProcessing}>
                        Enroll
                      </Button>
                    </div>
                  </div>
                </PaymentForm>
              </Elements>
            </div>
          </div>
        </div>
      </Modal.Content>
    </>
  )
}
